import hash from 'object-hash'

import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  getCompassAPIChartData,
  getDataSources,
} from '../utils/compass'
import { chartPalette, datetimeXAxis } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

interface DataPoint<T> {
  [timestamp: string]: T
}

interface Sentinel5PChartData extends CompassAPIDataBase {
  title: string
  subtitle?: string
  data: {
    holidays: DataPoint<string>[]
    no2_series: DataPoint<number>[]
    predicted_series: DataPoint<number>[]
    prediction_interval_bottom: DataPoint<number>[]
    prediction_interval_up: DataPoint<number>[]
  }
}

export namespace Satellite {
  export namespace Sentinel5P {
    const CONTEXT_ID = 'equity:satellite:sentinel5p'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      context: typeof CONTEXT_ID
    }

    export type ChartData = Sentinel5PChartData

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const getData = (series: DataPoint<number>[]) =>
        Object.entries(series).map((el) => [new Date(el[0]).getTime(), el[1]])

      const getMarker = (pointName: string | undefined) => ({
        enabled: true,
        fillColor: '#FFFFFF',
        lineColor: '#000',
        lineWidth: 1,
        pointName,
        radius: 3,
        symbol: 'diamond',
      })

      const tooltip = {
        enabled: true,
        pointFormatter: function (this: {
          color?: string
          y: number
          marker: { pointName: string }
          series?: {
            color: string | undefined
            name: string | undefined
          }
        }) {
          return `<span>${this.series?.name} </span>
          <span style="color:${this.color}">●</span> 
           <b>${this.y.toFixed(2)}</b><br/>${this?.marker?.pointName ?? ''}`
        },
        useHTML: true,
      }

      const commonConfig = {
        tooltip,
        yAxis: 'default',
        zIndex: 1,
      }

      const series = [
        {
          ...commonConfig,
          data: getData(data.data.no2_series).map((point) => {
            let pointName
            return [
              ...point,
              ...(Object.entries(data.data.holidays)
                .map((holiday) => [new Date(holiday[0]).getTime(), holiday[1]])
                .find((holiday) => {
                  pointName = holiday[1]
                  return holiday[0] === point[0]
                })
                ? [getMarker(pointName)]
                : []),
            ]
          }),
          id: 'no2-series',
          marker: { enabled: true, radius: 0 },
          name: 'NO2',
        },
        {
          ...commonConfig,
          data: getData(data.data.predicted_series),
          id: 'predicted-series',
          marker: { enabled: false },
          name: 'Previsão',
        },
        {
          color: '#BED8B6',
          data: getData(data.data.prediction_interval_up).map((point, idx) => [
            ...point,
            Object.values(data.data.prediction_interval_bottom)[idx],
          ]),
          enableMouseTracking: false,
          fillOpacity: 1,
          name: 'Intervalo de predição',
          type: 'arearange',
          yAxis: 'default',
          zIndex: 0,
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [{ id: 'default', title: { text: '' } }]

      return {
        id: Context.getChartId(config),
        title: 'Coluna troposférica de NO2 (Validez > 50)',
        options: mergeDeepAll([
          creditsConfig,
          {
            chart: { zoomType: 'x' },
            colors: chartPalette,
            series,
            plotOptions: { line: { keys: ['x', 'y', 'marker'] } },
            xAxis: datetimeXAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }
}
